import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@backstage/core-plugin-api';
import { useAsyncEntity } from '@backstage/plugin-catalog-react';
import { scaffolderApiRef } from '@backstage/plugin-scaffolder-react';
import {
  invalidateArgoEnvStatusesCache,
  useArgoEnvStatuses,
} from '../argocd/useArgoEnvStatuses';
import { Snackbar } from '../ui';
import { useT } from '../../modules/i18n';
import { parseDeployments } from './shared';
import { DeploymentsCardView } from './DeploymentsCardView';

const KUBERNETES_LABEL_SELECTOR_ANNOTATION =
  'backstage.io/kubernetes-label-selector';

// Only Deployment/StatefulSet-backed workloads can be rolling-restarted. Map
// each restartable component type to its `app.kubernetes.io/component` label so
// the rollout targets exactly one workload (e.g. the alert *service*, not the
// alert *event-handler* that shares the name). Knative kservices, cronjobs,
// jobs and migrations are not rolling-restartable — the button is hidden.
const COMPONENT_BY_TYPE: Record<string, string> = {
  service: 'service',
  'ai-service': 'service',
  'event-handler': 'event-handler',
  frontend: 'frontend',
  gateway: 'gateway',
};

interface SnackbarState {
  open: boolean;
  severity: 'success' | 'error' | 'warning' | 'info';
  title?: string;
  message?: string;
  action?: { label: string; onPress: () => void };
}

const rolloutNeedsApproval = (env: string) =>
  env !== 'development' && env !== 'staging';

// Annotation parsing lives in ./shared (single module shared with the
// entity-table cells); the presentational view lives in DeploymentsCardView.tsx
// so Storybook can render it without the @backstage/plugin-catalog-react /
// core-plugin-api runtime. Re-exported for the existing card consumers/tests.
export { parseDeployments } from './shared';
export type { DeploymentRow, HistoryEntry } from './shared';
export { DeploymentsCardView, HistoryPanel } from './DeploymentsCardView';
export type { DeploymentsCardViewProps } from './DeploymentsCardView';

/**
 * The Deployments card — reads the entity from context and the live ArgoCD
 * statuses, then hands them to the presentational DeploymentsCardView.
 */
export function DeploymentsCard() {
  const t = useT();
  // useAsyncEntity (not useEntity) so the card can re-read the entity on demand:
  // the promoted-tag columns come from the regask.io/deployments annotation,
  // which the gitops provider updates on a GitHub push webhook. The entity is a
  // one-time snapshot otherwise, so without refresh() the tags stay stale until
  // a full page reload even though the catalog backend already has the new ones.
  const { entity, refresh } = useAsyncEntity();
  const navigate = useNavigate();
  const scaffolderApi = useApi(scaffolderApiRef);
  const rows = useMemo(
    () => (entity ? parseDeployments(entity) : []),
    [entity],
  );
  const argoStatuses = useArgoEnvStatuses();
  const [lastSync, setLastSync] = useState(() => new Date());
  const [rollingOut, setRollingOut] = useState<ReadonlySet<string>>(new Set());
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    severity: 'info',
  });

  // Build the exact label selector for THIS service's workload: the entity's
  // own name selector plus its component label. Only defined for restartable
  // types — otherwise the Rollout button is hidden entirely.
  const component = COMPONENT_BY_TYPE[String(entity?.spec?.type ?? '')];
  const nameSelector =
    entity?.metadata.annotations?.[KUBERNETES_LABEL_SELECTOR_ANNOTATION];
  const rolloutSelector =
    component && nameSelector
      ? `${nameSelector},app.kubernetes.io/component=${component}`
      : undefined;

  const onRollout = async (env: string) => {
    if (!rolloutSelector) return;
    setRollingOut(prev => new Set(prev).add(env));
    try {
      const { taskId } = await scaffolderApi.scaffold({
        templateRef: 'template:default/rollout',
        values: {
          // services satisfies the template's required field; the selector is
          // what actually targets the one workload.
          services: [],
          selector: rolloutSelector,
          environment: env,
        } as unknown as Parameters<typeof scaffolderApi.scaffold>[0]['values'],
      });
      setSnackbar({
        open: true,
        severity: 'success',
        title: t('ui.deployments.rolloutStartedTitle', { env }),
        message: rolloutNeedsApproval(env)
          ? t('ui.deployments.rolloutApprovalMessage')
          : undefined,
        action: {
          label: t('ui.deployments.viewTaskAction'),
          onPress: () => navigate(`/actions/runs/${taskId}`),
        },
      });
    } catch (e) {
      setSnackbar({
        open: true,
        severity: 'error',
        title: t('ui.deployments.rolloutFailedTitle'),
        message: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setRollingOut(prev => {
        const next = new Set(prev);
        next.delete(env);
        return next;
      });
    }
  };

  return (
    <>
      <DeploymentsCardView
        rows={rows}
        argoStatuses={argoStatuses}
        lastSync={lastSync}
        onRefresh={() => {
          setLastSync(new Date());
          invalidateArgoEnvStatusesCache();
          // Re-read the entity so promoted tags / source / published catch up
          // with the latest gitops push without a full page reload.
          refresh?.();
        }}
        onRollout={rolloutSelector ? onRollout : undefined}
        rollingOut={rollingOut}
      />
      {/* Persistent (no auto-hide) so the View task link stays available. */}
      <Snackbar
        open={snackbar.open}
        severity={snackbar.severity}
        title={snackbar.title}
        action={snackbar.action}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        {snackbar.message}
      </Snackbar>
    </>
  );
}
